/**
 * GSB Configuration - Core settings and tenant management
 * Consolidated version with improved tenant handling
 */
import { BrowserRegistry } from "../registry.js";
const runtime = globalThis;
const isBrowser = runtime.window !== undefined;
// Storage keys
const STORAGE = {
    ACTIVE_TENANT: "gsb_active_tenant",
    TOKEN_PREFIX: "gsb_auth_",
    USER_PREFIX: "gsb_user_",
};
const GSB_SETTINGS = {
    TENANT_CODE: undefined,
    TOKEN: undefined,
    BASE_DOMAIN: undefined,
    API_URL: undefined,
    LANG_STORAGE_KEY: undefined,
    LANG_CODE: undefined,
};
function getSettings() {
    return typeof window === "undefined"
        ? GSB_SETTINGS
        : BrowserRegistry.acquire("config", () => GSB_SETTINGS);
}
// Default cache duration (infinite)
const DEFAULT_CACHE_DURATION = Number.MAX_SAFE_INTEGER; // Infinite duration
// Node 26 exposes a `localStorage` global that warns on every access unless started with
// --localstorage-file, so probe it only where web storage is actually meaningful.
function hasLocalStorage() {
    return isBrowser && runtime.localStorage !== undefined;
}
export function setLocalStorage(key, value, duration = DEFAULT_CACHE_DURATION) {
    if (hasLocalStorage()) {
        try {
            const storageData = {
                data: value,
                timestamp: Date.now(),
                expiry: Date.now() + duration,
                isPrimitive: true, // strings are primitives
            };
            runtime.localStorage?.setItem(key, JSON.stringify(storageData));
        }
        catch (error) {
            console.error(`Error storing data in localStorage with key ${key}:`, error);
        }
    }
}
export function getLocalStorage(key) {
    if (hasLocalStorage()) {
        try {
            const storedData = runtime.localStorage?.getItem(key);
            if (storedData) {
                try {
                    const parsedData = JSON.parse(storedData);
                    // Check if it's in the new format (has expiry)
                    if (typeof parsedData === "object" &&
                        parsedData !== null &&
                        "expiry" in parsedData &&
                        typeof parsedData.expiry === "number") {
                        // Check if data is still valid
                        if (Date.now() < parsedData.expiry) {
                            return "data" in parsedData && typeof parsedData.data === "string"
                                ? parsedData.data
                                : null;
                        }
                        else {
                            // Data expired, remove from localStorage
                            runtime.localStorage?.removeItem(key);
                            return null;
                        }
                    }
                    else {
                        // It's in the old format (direct string) or custom format
                        // Just return it as-is for backward compatibility
                        return storedData;
                    }
                }
                catch {
                    // If we can't parse as JSON, it's likely a direct string from old version
                    return storedData;
                }
            }
        }
        catch (error) {
            console.error(`Error retrieving data from localStorage with key ${key}:`, error);
        }
        return null;
    }
    return null;
}
export function removeLocalStorage(key) {
    if (hasLocalStorage()) {
        runtime.localStorage?.removeItem(key);
    }
}
// Environment variable access helper
export function getEnvVar(key, defaultValue) {
    return runtime.ENV?.[key] ?? defaultValue;
}
export function checkAllEnvVars(varName) {
    const nextVarName = `NEXT_PUBLIC_${varName}`;
    const viteVarName = `VITE_${varName}`;
    const envVarName = `GSB_${varName}`;
    return (getEnvVar(varName, "") ||
        getEnvVar(nextVarName, "") ||
        getEnvVar(viteVarName, "") ||
        getEnvVar(envVarName, ""));
}
/**
 * Get the base domain for GSB services
 * @returns The base domain
 */
export function getGsbBaseDomain() {
    const settings = getSettings();
    if (settings.BASE_DOMAIN) {
        return settings.BASE_DOMAIN;
    }
    const domainFromEnv = checkAllEnvVars("GSB_BASE_DOMAIN");
    if (domainFromEnv) {
        getSettings().BASE_DOMAIN = domainFromEnv;
        return domainFromEnv;
    }
    throw new Error("GSB_BASE_DOMAIN is required when GSB_API_URL is not set");
}
/**
 * Get the API URL for GSB services
 * @returns The API URL
 */
export function getGsbApiUrl() {
    if (getSettings().API_URL) {
        return getSettings().API_URL;
    }
    const apiUrlFromEnv = checkAllEnvVars("GSB_API_URL");
    if (apiUrlFromEnv) {
        getSettings().API_URL = apiUrlFromEnv;
        return apiUrlFromEnv;
    }
    return undefined;
}
export function getGsbLangCode() {
    if (getSettings().LANG_CODE) {
        return getSettings().LANG_CODE;
    }
    const storedLangCode = getLocalStorage(getGsbLangStorageKey());
    if (storedLangCode) {
        return storedLangCode;
    }
    return undefined;
}
export function normalizeGsbLangCode(langCode) {
    const normalized = langCode?.trim().replaceAll("-", "_").toLowerCase();
    return normalized || undefined;
}
export function getGsbLangStorageKey() {
    const settings = getSettings();
    if (settings.LANG_STORAGE_KEY) {
        return settings.LANG_STORAGE_KEY;
    }
    const langStorageKeyFromEnv = checkAllEnvVars("GSB_LANG_STORAGE_KEY");
    if (langStorageKeyFromEnv) {
        getSettings().LANG_STORAGE_KEY = langStorageKeyFromEnv;
        return langStorageKeyFromEnv;
    }
    return "gsb_lang_code";
}
/**
 * Get the default tenant code from environment variables
 * @returns The default tenant code from environment variables, or an empty string if not found.
 */
export function getDefaultTenant() {
    return checkAllEnvVars("GSB_TENANT_CODE");
}
/**
 * Get the current GSB tenant code
 * Prioritizes GSB_SETTINGS, then environment variables, then localStorage.
 * @returns The current active tenant code
 */
export function getGsbTenantCode() {
    if (getSettings().TENANT_CODE) {
        return getSettings().TENANT_CODE;
    }
    const envTenant = checkAllEnvVars("GSB_TENANT_CODE");
    if (envTenant) {
        getSettings().TENANT_CODE = envTenant;
        return envTenant;
    }
    const storedTenant = getLocalStorage(STORAGE.ACTIVE_TENANT);
    if (storedTenant) {
        getSettings().TENANT_CODE = storedTenant;
        return storedTenant;
    }
    const token = getSettings().TOKEN;
    if (token) {
        const extractedTenant = extractTenantCodeFromToken(token);
        if (extractedTenant) {
            getSettings().TENANT_CODE = extractedTenant;
            setLocalStorage(STORAGE.ACTIVE_TENANT, extractedTenant);
            return extractedTenant;
        }
    }
    // If no tenant code is found anywhere, it might return an empty string
    // or you might want to throw an error or return a specific default.
    // For now, it implicitly returns undefined if nothing is found, which becomes empty string via `?? ''` in some uses.
    // Let's explicitly set it to empty string if nothing found.
    getSettings().TENANT_CODE = "";
    return "";
}
/**
 * Set the active GSB tenant code
 * @param tenantCode The tenant code to set as active
 */
export function setGsbTenantCode(tenantCode) {
    getSettings().TENANT_CODE = tenantCode;
    setLocalStorage(STORAGE.ACTIVE_TENANT, tenantCode);
}
/**
 * Set the GSB API URL.
 * @param apiUrl The API URL to set.
 */
export function setGsbApiUrl(apiUrl) {
    getSettings().API_URL = apiUrl;
}
export function setGsbBaseDomain(baseDomain) {
    getSettings().BASE_DOMAIN = baseDomain;
}
/**
 * Set the GSB language code.
/**
 * Set the GSB language code.
 * @param langCode The language code to set.
 */
export function setGsbLangCode(langCode) {
    getSettings().LANG_CODE = langCode;
    if (langCode) {
        setLocalStorage(getGsbLangStorageKey(), langCode);
    }
}
export function setGsbLangStorageKey(langStorageKey) {
    getSettings().LANG_STORAGE_KEY = langStorageKey;
}
/**
 * Set the GSB token.
 * @param token The token to set.
 */
export function setGsbToken(token) {
    getSettings().TOKEN = token;
    if (token) {
        const extractedTenant = extractTenantCodeFromToken(token);
        if (extractedTenant) {
            // If getSettings().TENANT_CODE is not already set, or if it's different, update it.
            // This ensures consistency if a token for a different tenant is set.
            if (!getSettings().TENANT_CODE ||
                getSettings().TENANT_CODE !== extractedTenant) {
                setGsbTenantCode(extractedTenant); // This will also update localStorage
            }
        }
        const tenantCode = getGsbTenantCode();
        if (tenantCode) {
            removeLegacyTokenStorage(tenantCode);
        }
    }
    else {
        const tenantCode = getGsbTenantCode();
        if (tenantCode) {
            removeLocalStorage(getTokenStorageKey(tenantCode));
        }
    }
}
/**
 * Get the GSB token.
 * Prioritizes GSB_SETTINGS, then environment variables, then the current tenant cookie.
 */
export function getGsbToken() {
    if (getSettings().TOKEN) {
        return getSettings().TOKEN;
    }
    const envToken = checkAllEnvVars("GSB_TOKEN");
    if (envToken) {
        getSettings().TOKEN = envToken;
        return envToken;
    }
    const currentTenant = getGsbTenantCode(); // Ensures tenant context is resolved
    if (currentTenant) {
        const storedToken = getTokenFromCookie(currentTenant);
        if (storedToken) {
            getSettings().TOKEN = storedToken;
            return storedToken;
        }
    }
    getSettings().TOKEN = ""; // Default to empty string if no token found
    return "";
}
/**
 * Check if multi-tenant mode is enabled
 */
export function isMultiTenantEnabled() {
    return checkAllEnvVars("ENABLE_MULTI_TENANT") === "true";
}
/**
 * Parse tenant code from hostname
 * @param hostname The hostname to extract tenant from
 * @returns Tenant code or null if using main domain
 */
export function getTenantFromHostname(hostname) {
    // In production, check for subdomain
    const parts = hostname.split(".");
    if (parts.length > 2 && parts[0] !== "www" && parts[0] !== "app") {
        return parts[0] ?? null;
    }
    // Otherwise it's the main domain (default tenant)
    return null;
}
/**
 * Extract tenant code from a JWT token
 * @param token The JWT token
 * @returns The tenant code or undefined
 */
export function extractTenantCodeFromToken(token) {
    try {
        const [, base64Url] = token.split(".");
        if (!base64Url) {
            return undefined;
        }
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const payload = JSON.parse(runtime.atob(base64));
        if (typeof payload === "object" &&
            payload !== null &&
            "tc" in payload &&
            typeof payload.tc === "string") {
            return payload.tc || undefined;
        }
        return undefined;
    }
    catch {
        return undefined;
    }
}
/**
 * Get a token from environment variables based on tenant code (for development)
 * @param tenantCode The tenant code
 * @returns The token from environment if available, or null
 */
export function getEnvToken() {
    return getGsbToken();
}
/**
 * Storage key helper for tokens
 * @param tenantCode The tenant code
 * @returns Storage key for the tenant's token
 */
export function getTokenStorageKey(tenantCode) {
    return `${STORAGE.TOKEN_PREFIX}${tenantCode}`;
}
export function getTokenFromCookie(tenantCode) {
    removeLegacyTokenStorage(tenantCode);
    if (typeof document === "undefined")
        return undefined;
    const prefix = `${getTokenStorageKey(tenantCode)}=`;
    const cookie = document.cookie
        .split(";")
        .find((entry) => entry.trim().startsWith(prefix));
    return cookie?.trim().slice(prefix.length) || undefined;
}
function removeLegacyTokenStorage(tenantCode) {
    removeLocalStorage(getTokenStorageKey(tenantCode));
    const legacyToken = getLocalStorage(tenantCode);
    if (legacyToken && extractTenantCodeFromToken(legacyToken) === tenantCode) {
        removeLocalStorage(tenantCode);
    }
}
/**
 * Storage key helper for user data
 * @param tenantCode The tenant code
 * @returns Storage key for the tenant's user data
 */
export function getUserDataStorageKey(tenantCode) {
    return `${STORAGE.USER_PREFIX}${tenantCode}`;
}
/**
 * Get available tenants from storage
 */
export function getAvailableTenants() {
    if (!isBrowser || !runtime.localStorage) {
        return [getDefaultTenant()];
    }
    try {
        const availableTenants = new Set();
        // Add current default
        availableTenants.add(getDefaultTenant());
        // Get active tenant if different
        const activeTenant = runtime.localStorage.getItem(STORAGE.ACTIVE_TENANT);
        if (activeTenant) {
            availableTenants.add(activeTenant);
        }
        // Find all tenants with tokens
        Object.keys(runtime.localStorage).forEach((key) => {
            if (key.startsWith(STORAGE.TOKEN_PREFIX)) {
                const tenantCode = key.replace(STORAGE.TOKEN_PREFIX, "");
                availableTenants.add(tenantCode);
            }
        });
        return Array.from(availableTenants);
    }
    catch (error) {
        console.error("Error getting available tenants:", error);
        return [getDefaultTenant()];
    }
}
/**
 * @deprecated Use setGsbTenantCode and appropriate auth service methods to clear tokens.
 */
export function clearGsbAuth() {
    getSettings().TOKEN = "";
    // getSettings().TENANT_CODE is not cleared here,
    // as tenant context might still be relevant or derived from other sources (e.g., hostname, default env).
    // If explicit tenant clearing is needed, use setGsbTenantCode('').
    // Also, explicitly remove token from localStorage for current tenant.
    if (hasLocalStorage()) {
        const currentTenant = getGsbTenantCode();
        if (currentTenant) {
            runtime.localStorage?.removeItem(getTokenStorageKey(currentTenant));
        }
        // Optionally, clear active tenant if that's desired behavior for clearGsbAuth
        // localStorage.removeItem(STORAGE.ACTIVE_TENANT);
    }
}
/**
 * GSB Environment Configuration
 */
export const GSB_CONFIG = {
    // Base domain configuration
    get BASE_DOMAIN() {
        return getGsbBaseDomain();
    },
    // Default tenant codes
    get TENANT_CODE() {
        return getGsbTenantCode();
    },
    // API URLs constructed using tenant and base domain
    API_URL(tenantCode, baseDomain) {
        const apiUrl = getGsbApiUrl();
        if (apiUrl) {
            return apiUrl;
        }
        const tenant = tenantCode || this.TENANT_CODE;
        const domain = baseDomain || this.BASE_DOMAIN;
        return `https://${tenant}.${domain}`;
    },
    // Language code
    get LANG_CODE() {
        return getGsbLangCode();
    },
    // Language storage key
    get LANG_STORAGE_KEY() {
        return getGsbLangStorageKey();
    },
    // Multi-tenant support
    get ENABLE_MULTI_TENANT() {
        return isMultiTenantEnabled();
    },
    // Function for extracting tenant code from token
    extractTenantCode: extractTenantCodeFromToken,
    // Helper function to build tenant-specific URL
    getTenantUrl(tenantCode) {
        if (!tenantCode) {
            throw new Error("Tenant code is required to construct URL");
        }
        const domain = this.BASE_DOMAIN;
        return `https://${tenantCode}.${domain}`;
    },
};
//# sourceMappingURL=gsb-config.js.map